// BhavnaDrishtiTab.tsx
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Smile } from "lucide-react";
import AstriaIndiaV2Shell, { SHELL_COLORS_INDIA_V2 } from "../AstriaIndiaV2Shell";
import {
  AstriaIndiaV2ResultCard,
  AstriaIndiaV2FieldBlock,
  AstriaIndiaV2HighlightBadge,
  AstriaIndiaV2SignatureLine,
  IndiaV2LoadingState,
  IndiaV2ErrorState,
  IndiaV2ChatCta,
} from "../AstriaIndiaV2Card";
import {
  WizardProgress,
  WizardStep,
  WizardNav,
  WizardEmojiWheel,
  WizardSlider,
  WizardTriChoice,
} from "../AstriaIndiaV2Wizard";
import { useAstriaIndiaV2Tab } from "../useAstriaIndiaV2Tab";
import { AstriaIndiaV2BhavnaData } from "../../../../contexts/ChatContext";
import { useIndiaV2Language } from "../../../../utils/India/astriaIndiaV2Language";
import { ASTRIA_INDIA_V2_COPY } from "../../../../utils/India/astriaIndiaV2Copy";

const SUBCATEGORY_NAME = "Bhavna Drishti";

const TOTAL_STEPS = 3;

const BhavnaDrishtiTab: React.FC = () => {
  // Which engine to call is decided ONLY by which sidebar entry the user
  // clicked ("Astria India V2" vs "Astria India V3" — see
  // Sidebar.tsx/TesterHome.tsx's "?v=3" query flag) and never changes for
  // the lifetime of this page — no in-page switch.
  const [searchParams] = useSearchParams();
  const version: "v2" | "v3" = searchParams.get("v") === "3" ? "v3" : "v2";
  const categoryName = version === "v3" ? "Astria India V3" : "Astria India V2";

  const [lang, setLang] = useIndiaV2Language();
  const copy = ASTRIA_INDIA_V2_COPY[lang];
  const t = copy.bhavna;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } = useAstriaIndiaV2Tab({
    categoryName,
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [step, setStep] = useState(1);
  const [feeling, setFeeling] = useState<string | null>(null);
  const [intensity, setIntensity] = useState(50);
  const [location, setLocation] = useState<string | null>(null);

  const started = !!message || loading || !!error;

  const buildMessage = () => {
    const feelingLabel = feeling ? shared.emojiLabels[feeling as keyof typeof shared.emojiLabels] || feeling : "unsettled";
    const locationLabel =
      t.locationOptions.find((l) => l.value === location)?.label || t.locationOptions[2].label;

    return `Right now I'm feeling ${feelingLabel}, at about ${intensity}/100 intensity. It sits mostly in my ${locationLabel.toLowerCase()}. Please give me a Bhavna Drishti emotional inner-weather reading.`;
  };

  const handleSubmit = () => {
    send(buildMessage());
  };

  const bhavnaData = message?.astriaIndiaV2Data as AstriaIndiaV2BhavnaData | undefined;

  if (started) {
    return (
      <AstriaIndiaV2Shell
        active="bhavna"
        lang={lang}
        onLangChange={setLang}
        version={version}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#2E1F45", marginBottom: 4 }}>
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: "#8C7DA0" }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <IndiaV2LoadingState label={t.loading} />}

        {!loading && error && (
          <IndiaV2ErrorState message={error} onRetry={handleSubmit} retryLabel={shared.tryAgain} />
        )}

        {!loading && !error && bhavnaData && (
          <>
            <AstriaIndiaV2ResultCard
              icon={<Smile className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              accent={["#D98CA0", "#C9A9E8"]}
              shareText={`${t.tabLabel}\n\n${bhavnaData.emotional_state}\n\n${bhavnaData.soft_landing}`}
              shareLabels={shared}
            >
              <AstriaIndiaV2HighlightBadge text={bhavnaData.highlight} />
              <AstriaIndiaV2FieldBlock label={t.fields.emotionalState} value={bhavnaData.emotional_state} />
              <AstriaIndiaV2SignatureLine value={bhavnaData.micro_imagery} />
              <AstriaIndiaV2SignatureLine value={bhavnaData.astro_soft_influence} />
              <AstriaIndiaV2FieldBlock label={t.fields.rootPattern} value={bhavnaData.root_pattern} />
              <AstriaIndiaV2FieldBlock label={t.fields.currentWeight} value={bhavnaData.current_weight} />
              <AstriaIndiaV2FieldBlock label={t.fields.innerRoom} value={bhavnaData.inner_room_imagery} />
              <AstriaIndiaV2FieldBlock label={t.fields.softLanding} value={bhavnaData.soft_landing} />
              <AstriaIndiaV2SignatureLine value={bhavnaData.clarity_point} emphasis />

              {bhavnaData.actions?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: SHELL_COLORS_INDIA_V2.accent,
                      marginBottom: 8,
                    }}
                  >
                    {t.fields.actions}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {bhavnaData.actions.map((a, i) => (
                      <li
                        key={i}
                        style={{ fontSize: 14.5, lineHeight: 1.7, color: "#4A3B5C", marginBottom: 4 }}
                      >
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </AstriaIndiaV2ResultCard>
            <button
              onClick={() => {
                reset();
                setStep(1);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: "#8C7DA0" }}
            >
              {shared.startNewReading}
            </button>
            <IndiaV2ChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !bhavnaData && message && (
          <AstriaIndiaV2ResultCard
            icon={<Smile className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
            accent={["#D98CA0", "#C9A9E8"]}
          >
            <p style={{ fontSize: 15, lineHeight: 1.75, color: "#4A3B5C", whiteSpace: "pre-wrap" }}>
              {message.content}
            </p>
          </AstriaIndiaV2ResultCard>
        )}
      </AstriaIndiaV2Shell>
    );
  }

  return (
    <AstriaIndiaV2Shell
      active="bhavna"
      lang={lang}
      onLangChange={setLang}
      version={version}
    >
      <WizardProgress
        step={step}
        total={TOTAL_STEPS}
        accent="#D98CA0"
        stepLabel={shared.stepOf}
        percentLabel={shared.percentComplete}
      />

      {step === 1 && (
        <WizardStep stepKey="feeling" question={t.step1Question}>
          <WizardEmojiWheel value={feeling} onChange={setFeeling} labels={shared.emojiLabels} />
          <WizardNav
            showBack={false}
            nextDisabled={!feeling}
            onNext={() => setStep(2)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 2 && (
        <WizardStep stepKey="intensity" question={t.step2Question}>
          <WizardSlider value={intensity} onChange={setIntensity} lowLabel={t.intensityLow} highLabel={t.intensityHigh} />
          <WizardNav
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 3 && (
        <WizardStep stepKey="location" question={t.step3Question}>
          <WizardTriChoice options={t.locationOptions} value={location} onChange={setLocation} />
          <WizardNav
            onBack={() => setStep(2)}
            nextLabel={t.revealButton}
            nextDisabled={!location}
            onNext={handleSubmit}
            backLabel={shared.back}
          />
        </WizardStep>
      )}
    </AstriaIndiaV2Shell>
  );
};

export default BhavnaDrishtiTab;
